import React from 'react';
import { CheckCircle, Award, Users, TrendingUp } from 'lucide-react';
import { companyInfo, stats } from '../data/mock';

export const About = () => {
  const foundedYear = new Date(companyInfo.founded).getFullYear();
  const yearsInBusiness = new Date().getFullYear() - foundedYear;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#003d82] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Sobre a Litoral Norte</h1>
            <p className="text-xl text-gray-200">
              Uma empresa familiar dedicada ao transporte rodoviário de passageiros há mais de duas décadas.
            </p>
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#003d82] mb-6">A Nossa História</h2>
              <div className="space-y-4 text-gray-700">
                <p className="text-lg">
                  Fundada a <strong>3 de março de 2003</strong>, a Litoral Norte - Transportes Rodoviários de Passageiros, Lda. nasceu com a missão de proporcionar serviços de transporte de qualidade na região da Póvoa de Varzim e Vila do Conde.
                
                </p>
                <p className="text-lg">
                  Ao longo destes <strong>{yearsInBusiness} anos</strong>, temos vindo a crescer e a consolidar a nossa posição como um dos principais operadores de transporte da região. Com uma frota moderna de <strong>{companyInfo.fleetSize} autocarros</strong>, servimos centenas de passageiros todos os dias através das nossas carreiras UNIR e serviços especializados.
                
                </p>
                <p className="text-lg">
                  O nosso compromisso com a excelência, segurança e pontualidade tem sido a chave do nosso sucesso. 
                  Investimos continuamente na modernização da nossa frota e na formação dos nossos colaboradores para 
                  garantirmos o melhor serviço aos nossos clientes.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_povoa-transport/artifacts/xgcpvkn8_IMG_0617.JPEG"
                alt="Autocarro Litoral Norte Branco"
                className="rounded-2xl shadow-2xl" />


            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) =>
            <div
              key={index}
              className="text-center p-6 rounded-xl bg-white shadow-md hover:shadow-xl transition-all duration-300">

                <div className="text-4xl md:text-5xl font-bold text-[#003d82] mb-2">
                  {stat.number}{stat.suffix}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#003d82] mb-4">Os Nossos Valores</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Valores que nos guiam no dia a dia e que fazem a diferença nos nossos serviços
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
            {
              icon: CheckCircle,
              title: 'Segurança',
              description: 'A segurança dos nossos passageiros é a nossa prioridade máxima. Frota bem mantida e motoristas certificados.'
            },
            {
              icon: Award,
              title: 'Excelência',
              description: "Procuramos a excel\xEAncia em tudo o que fazemos, desde o atendimento ao cliente at\xE9 \xE0 manuten\xE7\xE3o dos ve\xEDculos."
            },
            {
              icon: Users,
              title: 'Compromisso',
              description: 'Comprometidos com a comunidade e com a prestação de um serviço público de qualidade.'
            },
            {
              icon: TrendingUp,
              title: 'Inovação',
              description: 'Investimos continuamente em tecnologia e modernização para oferecer o melhor serviço.'
            }].
            map((value, index) =>
            <div
              key={index}
              className="text-center p-8 rounded-xl bg-gradient-to-br from-[#003d82]/5 to-transparent hover:shadow-lg transition-all duration-300">

                <div className="w-16 h-16 rounded-full bg-[#f5a623] flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} className="text-[#003d82]" />
                </div>
                <h3 className="text-xl font-bold text-[#003d82] mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#003d82] mb-4">Onde Nos Encontrar</h2>
              <p className="text-xl text-gray-600">
                Visite o nosso escritório no Centro Comercial Premar
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="grid md:grid-cols-2">
                <div className="p-8 md:p-12">
                  <h3 className="text-2xl font-bold text-[#003d82] mb-6">Informações de Contacto</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Morada</div>
                      <div className="text-gray-600">
                        {companyInfo.address.street}<br />
                        {companyInfo.address.lojas}<br />
                        {companyInfo.address.postal} {companyInfo.address.city}
                      </div>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Telefone</div>
                      <a href={`tel:${companyInfo.phone}`} className="text-[#003d82] hover:text-[#f5a623]">
                        {companyInfo.phone}
                      </a>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Email</div>
                      <a href={`mailto:${companyInfo.email}`} className="text-[#003d82] hover:text-[#f5a623]">
                        {companyInfo.email}
                      </a>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 mb-1">Horário</div>
                      <div className="text-gray-600">
                        Segunda a Sexta: {companyInfo.hours.weekdays}<br />
                        Sábado, Domingo e Feriados: {companyInfo.hours.sunday}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="h-[400px] md:h-auto">
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3004.8!2d${companyInfo.coordinates.lng}!3d${companyInfo.coordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDHCsDIzJzE2LjQiTiA4wrA0NSczOS43Ilc!5e0!3m2!1spt-PT!2spt!4v1234567890!5m2!1spt-PT!2spt`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Localização Litoral Norte" />

                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>);

};